export interface PageOption {
  key: string;
  label: string;
  children?: PageOption[];
}

// Keys superseded by the consolidated navigation. Purged on save so saved
// tenant/user pages contain only the current normalized keys.
export const LEGACY_PAGE_KEYS: Set<string> = new Set([
  "SCENARIO_MODELING",
  "PRODUCT_HUB",
  "TRADE_DATA",
  "DOCUMENTS",
  "POST_IMPORT_ADJUSTMENT",
]);

export const PAGE_OPTIONS: PageOption[] = [
  { key: "ANALYTICS", label: "Analytics" },
  {
    key: "CUSTOMS_ERP",
    label: "Customs ERP",
    children: [
      { key: "CUSTOMS_ERP_PRODUCTS", label: "Products" },
      { key: "CUSTOMS_ERP_SUPPLIERS", label: "Suppliers" },
      { key: "CUSTOMS_ERP_ENTRIES", label: "Entries" },
      { key: "CUSTOMS_ERP_DOCUMENTS", label: "Documents" },
    ],
  },
  { key: "CHAT_ASSISTANCE", label: "Riffs Intelligence" },
  {
    key: "COMPLIANCE",
    label: "Compliance",
    children: [
      { key: "COMPLIANCE_VERIFICATION", label: "Verification" },
      { key: "COMPLIANCE_TARIFF_ANALYSIS", label: "Tariff Analysis" },
      { key: "COMPLIANCE_POST_IMPORT", label: "Post Import" },
      { key: "COMPLIANCE_CERTIFICATE_OF_ORIGIN", label: "Certificate of Origin" },
      { key: "COMPLIANCE_TARIFF_ENGINEERING", label: "Tariff Engineering" },
    ],
  },
  {
    key: "TOOLS",
    label: "Tools",
    children: [
      { key: "TOOLS_DUTY_CALCULATOR", label: "Duty Calculator" },
      { key: "TOOLS_UNIT_COST_ANALYSIS", label: "Unit Cost Analysis" },
      { key: "TOOLS_COMMERCE_DATA", label: "Commerce Data" },
      { key: "TOOLS_SCENARIO_MODELING", label: "Scenario Modeling" },
      { key: "TOOLS_IEEPA_REFUND_CAPE", label: "IEEPA Refund Cape" },
    ],
  },
  {
    key: "POTENTIAL_SAVINGS",
    label: "Potential Savings",
    children: [
      { key: "FIRST_SALE", label: "First Sale" },
      { key: "POTENTIAL_SAVINGS_PREFERENTIAL_ORIGIN", label: "Preferential Origin" },
      { key: "POTENTIAL_SAVINGS_FTZ", label: "FTZ" },
      { key: "POTENTIAL_SAVINGS_301_EXCLUSIONS", label: "301 Exclusions" },
    ],
  },
];

// Get all keys from a PageOption (child keys only for parents, key for flat)
const getAllKeys = (option: PageOption): string[] => {
  if (!option.children) return [option.key];
  return option.children.map((c) => c.key);
};

// Collect every key (parent + children) from an options tree
export const collectAllKeys = (options: PageOption[]): Set<string> => {
  const keys = new Set<string>();
  for (const opt of options) {
    keys.add(opt.key);
    if (opt.children) for (const c of opt.children) keys.add(c.key);
  }
  return keys;
};

// Normalize selection for storage:
// - All children selected → store parent key only
// - Partial children → store only selected child keys
export const normalizeForStorage = (
  selectedKeys: string[],
  options: PageOption[]
): string[] => {
  const result: string[] = [];
  const handled = new Set<string>();
  for (const option of options) {
    handled.add(option.key);
    if (!option.children) {
      if (selectedKeys.includes(option.key)) {
        result.push(option.key);
      }
      continue;
    }
    const childKeys = getAllKeys(option);
    for (const k of childKeys) handled.add(k);
    const selectedChildren = childKeys.filter((k) => selectedKeys.includes(k));
    if (selectedChildren.length === childKeys.length) {
      result.push(option.key);
    } else {
      result.push(...selectedChildren);
    }
  }
  // Preserve keys not covered by the options tree (e.g. PRODUCT_HUB_DUTY_COLUMNS),
  // but drop known legacy keys so saved pages only contain normalized keys.
  for (const key of selectedKeys) {
    if (!handled.has(key) && !LEGACY_PAGE_KEYS.has(key)) result.push(key);
  }
  return result;
};

// Expand stored keys for display:
// - Parent key → mark all children as selected
export const expandForDisplay = (
  storedKeys: string[],
  options: PageOption[]
): string[] => {
  const result = new Set(storedKeys);
  for (const option of options) {
    if (!option.children) continue;
    if (result.has(option.key)) {
      for (const child of option.children) {
        result.add(child.key);
      }
    }
  }
  return Array.from(result);
};

// Filter options by available tenant keys
export const filterOptionsByAvailable = (
  options: PageOption[],
  availableKeys: string[]
): PageOption[] => {
  return options
    .map((option) => {
      if (!option.children) {
        return availableKeys.includes(option.key) ? option : null;
      }
      // Parent is available if the parent key or any child key is in available
      const parentAvailable = availableKeys.includes(option.key);
      const filteredChildren = option.children.filter(
        (c) => parentAvailable || availableKeys.includes(c.key)
      );
      if (filteredChildren.length === 0) return null;
      return { ...option, children: filteredChildren };
    })
    .filter((o): o is PageOption => o !== null);
};
